import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

/**
 * Composant de sélection de source d'approvisionnement
 * Permet de choisir entre un site interne ou un distributeur
 * @param {function} onChange - Fonction appelée lors du changement de valeur
 * @param {object} value - Valeur actuelle
 * @param {boolean} disabled - Si le composant est désactivé
 * @param {string} categoryFilter - Filtre de catégorie ('internal' ou 'distributor')
 */
const STATIC_ZONES = ['EST', 'EST 2', 'CENTRE EST', 'CENTRE', 'CENTRE-B', 'SUD', 'CENTRE OUEST', 'OUEST'];

const SupplySourceSelector = ({ onChange, value, disabled = false, categoryFilter = null }) => {
  const [loading, setLoading] = useState(true);
  const [internalSites, setInternalSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(
    categoryFilter || value?.category || 'internal'
  );
  const [selectedSourceId, setSelectedSourceId] = useState(value?.source?.name || value?.source?.id || '');
  const [error, setError] = useState(false);

  // Sites internes par défaut en cas d'erreur
  const defaultInternalSites = [
    { _id: 'sib', name: 'Site Industriel Bejaia', code: 'SIB' },
    { _id: 'sia', name: 'Site Industriel Alger', code: 'SIA' },
    { _id: 'sis', name: 'Site Industriel Sétif', code: 'SIS' },
  ];

  // Charger les sources d'approvisionnement au chargement du composant
  useEffect(() => {
    fetchSupplySources();
  }, []);

  // Mettre à jour la catégorie et l'ID de la source lorsque la valeur change
  useEffect(() => {
    if (value) {
      // Si un filtre de catégorie est défini, l'utiliser, sinon utiliser la valeur
      setSelectedCategory(categoryFilter || value.category || 'internal');
      setSelectedSourceId(value.source?.id || '');
    }
  }, [value, categoryFilter]);

  // Récupérer les sources d'approvisionnement depuis l'API
  const fetchSupplySources = async () => {
    try {
      setLoading(true);
      setError(false);

      try {
        const internalSitesResponse = await api.internalSites.getAll();
        setInternalSites(internalSitesResponse.data.data || []);
      } catch (error) {
        console.error('Error fetching internal sites:', error);
        setInternalSites(defaultInternalSites);
      }

      try {
        const zonesResponse = await api.zones.getAll();
        const activeZones = (zonesResponse.data.data || []).filter(z => z.isActive);
        if (activeZones.length > 0) {
          // Zones créées en base — utiliser les vrais IDs MongoDB
          setZones(activeZones.map(z => ({ _id: z._id, name: z.name })));
        } else {
          // Fallback : zones statiques (nom utilisé comme clé)
          setZones(STATIC_ZONES.map(name => ({ _id: name, name })));
        }
      } catch (error) {
        console.error('Error fetching zones:', error);
        setZones(STATIC_ZONES.map(name => ({ _id: name, name })));
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching supply sources:', error);
      setError(true);
      setInternalSites(defaultInternalSites);
      setLoading(false);
    }
  };

  // Gérer le changement de catégorie (interne ou distributeur)
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedSourceId(''); // Réinitialiser la source sélectionnée
    
    // Notifier le parent du changement
    if (onChange) {
      onChange({
        category,
        source: null
      });
    }
  };

  // Gérer le changement de source
  const handleSourceChange = (e) => {
    const sourceId = e.target.value;
    setSelectedSourceId(sourceId);

    let selectedSource = null;
    if (sourceId) {
      if (selectedCategory === 'internal') {
        const site = internalSites.find(s => s._id === sourceId);
        if (site) selectedSource = { id: site._id, name: site.name, code: site.code };
      } else {
        const zone = zones.find(z => z._id === sourceId || z.name === sourceId);
        if (zone) {
          // Si _id === name c'est un fallback statique (pas d'ObjectId réel)
          const isStatic = zone._id === zone.name;
          selectedSource = { name: zone.name, type: 'zone', ...(isStatic ? {} : { id: zone._id }) };
        }
      }
    }

    if (onChange) {
      onChange({ category: selectedCategory, source: selectedSource });
    }
  };

  // Sélectionner manuellement un site interne par défaut
  const selectDefaultSite = () => {
    if (internalSites.length > 0) {
      const defaultSite = internalSites[0];
      setSelectedSourceId(defaultSite._id);
      
      // Notifier le parent du changement
      if (onChange) {
        onChange({
          category: 'internal',
          source: {
            id: defaultSite._id,
            name: defaultSite.name,
            code: defaultSite.code
          }
        });
      }
    }
  };

  // Rendu du composant
  return (
    <div className="supply-source-selector">
      {/* Afficher les options de catégorie uniquement si aucun filtre n'est spécifié */}
      {!categoryFilter && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type de source d'approvisionnement
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio h-4 w-4 text-blue-600"
                checked={selectedCategory === 'internal'}
                onChange={() => handleCategoryChange('internal')}
                disabled={disabled}
              />
              <span className="ml-2">Site interne</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio h-4 w-4 text-blue-600"
                checked={selectedCategory === 'distributor'}
                onChange={() => handleCategoryChange('distributor')}
                disabled={disabled}
              />
              <span className="ml-2">Distributeur</span>
            </label>
          </div>
        </div>
      )}
      
      <div className="mb-4">
        <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-2">
          {selectedCategory === 'internal' ? 'Site interne' : 'Zone'}
        </label>
        {loading ? (
          <div className="animate-pulse h-10 bg-gray-200 rounded"></div>
        ) : (
          <select
            id="source"
            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            value={selectedSourceId}
            onChange={handleSourceChange}
            disabled={disabled}
          >
            <option value="">Sélectionner {selectedCategory === 'internal' ? 'un site' : 'une zone'}</option>
            {selectedCategory === 'internal' ? (
              internalSites.map(site => (
                <option key={site._id} value={site._id}>
                  {site.name} ({site.code})
                </option>
              ))
            ) : (
              zones.map(zone => (
                <option key={zone._id} value={zone._id}>
                  {zone.name}
                </option>
              ))
            )}
          </select>
        )}

        {error && (
          <p className="mt-2 text-sm text-orange-600">
            Impossible de charger les données depuis le serveur. Utilisation des valeurs par défaut.
          </p>
        )}

        {selectedCategory === 'internal' && internalSites.length === 0 && !loading && (
          <p className="mt-2 text-sm text-red-600">
            Aucun site interne disponible. Veuillez en créer un d'abord.
          </p>
        )}
        {selectedCategory === 'distributor' && zones.length === 0 && !loading && (
          <p className="mt-2 text-sm text-red-600">
            Aucune zone disponible. Veuillez en créer une d'abord.
          </p>
        )}

        {selectedCategory === 'internal' && internalSites.length > 0 && !selectedSourceId && (
          <button
            type="button"
            onClick={selectDefaultSite}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Utiliser le site par défaut (Site Industriel Bejaia)
          </button>
        )}
      </div>
    </div>
  );
};

export default SupplySourceSelector;
